import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';
import { logger } from '../../lib/logger';

interface ErrorBoundaryProps {
  children: ReactNode;
  level?: 'page' | 'component' | 'app';
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary component that catches JavaScript errors anywhere in the
 * child component tree, logs them, and displays a fallback UI.
 *
 * Usage:
 * - level="app": Full-screen error page for top-level crashes
 * - level="page": Card-style error for page/route failures
 * - level="component": Compact inline error for component failures
 *
 * @example
 * // App-level (in index.tsx)
 * <ErrorBoundary level="app">
 *   <App />
 * </ErrorBoundary>
 *
 * // Page-level (in routes)
 * <ErrorBoundary level="page">
 *   <MainApp />
 * </ErrorBoundary>
 *
 * // Component-level
 * <ErrorBoundary level="component">
 *   <SomeComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error for debugging
    logger.error('[ErrorBoundary] Caught error:', error);
    logger.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // Call optional error callback (useful for error tracking services)
    this.props.onError?.(error, errorInfo);

    // Future: Send to error tracking service like Sentry
    // if (!import.meta.env.DEV) {
    //   Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    // }
  }

  resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, level = 'page', fallback } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Use default ErrorFallback component
      return (
        <ErrorFallback
          error={error}
          resetErrorBoundary={this.resetErrorBoundary}
          level={level}
        />
      );
    }

    return children;
  }
}
